import React from "react";
import { styled } from "@mui/material/styles";
import { TablePagination } from "@mui/material";
import colors from "assets/theme-dark/base/colors";

interface TablePaginationWrapperProps {
  totalCount: number;
  page: number;
  rowsPerPage: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
}

export const MAX_ROWS = 50000;

const TablePaginationWrapper: React.FC<TablePaginationWrapperProps> = ({
  totalCount,
  page,
  rowsPerPage,
  hasNextPage,
  hasPreviousPage,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const rowsPerPageOptions = [5, 10, 20, 25, 50, 100, { label: "All Rows", value: MAX_ROWS }];

  const handlePageChange = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    onPageChange(event, newPage + 1);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onRowsPerPageChange(event);
  };

  const validRowsPerPage = rowsPerPageOptions.some(
    (option) => (typeof option === "number" ? option : option.value) === rowsPerPage
  )
    ? rowsPerPage
    : MAX_ROWS;

  const labelDisplayedRows = ({ from, to, count }: { from: number; to: number; count: number }) => {
    if (validRowsPerPage === MAX_ROWS) {
      return `1-${count} of ${count}`;
    }
    return `${from}-${to} of ${count}`;
  };

  return (
    <StyledTablePagination
      rowsPerPageOptions={rowsPerPageOptions}
      count={totalCount}
      rowsPerPage={validRowsPerPage}
      page={page - 1}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
      labelRowsPerPage="Rows per page:"
      labelDisplayedRows={labelDisplayedRows}
      nextIconButtonProps={{ disabled: !hasNextPage || validRowsPerPage === MAX_ROWS }}
      backIconButtonProps={{ disabled: !hasPreviousPage }}
    />
  );
};

const StyledTablePagination = styled(TablePagination)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(2),
  fontSize: "0.875rem",
  color: colors.text.primary,
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    fontSize: "0.875rem",
    color: colors.text.primary,
  },
  "& .MuiTablePagination-select": {
    fontSize: "0.875rem",
  },
  "& .MuiTablePagination-actions": {
    "& .MuiIconButton-root": {
      color: colors.text.primary,
    },
    "& .Mui-disabled": {
      color: colors.grey[500],
    },
  },
}));

export default TablePaginationWrapper;
